import type { AbstractPowerSyncDatabase } from "@powersync/common";
import { USER_DATA_TABLES } from "@vigia/powersync";

/**
 * Replaces VISITOR_ID with the newly authenticated user's ID
 * across all relevant tables and the offline CRUD queue.
 *
 * This ensures that:
 * 1. The local UI instantly reflects the new owner without flashing.
 * 2. The PowerSync upload queue sends the correct `user_id` to the backend.
 */
export async function sweepVisitorData(
  db: AbstractPowerSyncDatabase | null,
  newUserId: string,
  visitorId: string,
) {
  if (!db || !newUserId) {
    return;
  }

  try {
    // 1. Update the actual table rows so UI queries pick them up immediately
    await Promise.all(
      USER_DATA_TABLES.map(async (table) =>
        db.execute(`UPDATE ${table} SET user_id = ? WHERE user_id = ?`, [newUserId, visitorId]),
      ),
    );

    // 2. Rewrite the queued operations so the network payload has the correct id.
    //
    // PowerSync's `ps_crud.data` column is a JSON envelope
    // `{"op":"PUT","type":"<table>","id":"<uuid>","data":{<column>:<value>,…}}`
    // — the row's ACTUAL column values live NESTED under `$.data.*`, not at the top
    // level (see `CrudEntry.fromRow`: `opData = parsed.data`). So the visitor id
    // must be rewritten at `$.data.user_id`; the previous top-level `$.user_id`
    // path matched NOTHING, leaving the queued op stamped with `VISITOR_ID`. On an
    // org-scoped backend (field-check) a write still carrying the visitor id fails
    // the ownership/membership check on upload and is silently dropped, so the
    // guest's fieldwork never round-tripped after "Keep and merge". (Visitor domain
    // rows are personal — `organization_id` is NULL — so only user_id needs remap.)
    await db.execute(
      `
      UPDATE ps_crud
      SET data = json_set(data, '$.data.user_id', ?)
      WHERE json_extract(data, '$.data.user_id') = ?
    `,
      [newUserId, visitorId],
    );
  } catch (error) {
    console.error("[Sweep] Failed to sweep visitor data:", error);
  }
}
